import { useContext, useEffect, useState } from "react";
import { DataTable } from "../../components/bootstrap/data-table";
import { LoadingOverlay } from "../../components/layout/loading-overlay";
import { PortalMainLayout } from "../../components/layout/portal-main-layout";
import { AuthorisedPage } from "../auth/authorised-page";
import { CognitoContext } from "../../contexts/cognito/cognito-provider";
import { useNavigate, useParams, Link } from "react-router-dom";
import { PortalUserForm } from "../../components/users/edit-user";
import { CoreServiceSelector } from "../../services/core/service-selector";
import { consumeUserRecordPrefetch } from "../../services/core/users-prefetch";
import { AppRouteNames } from "../../routes/app-routes";
import { useAppLoading } from "../../components/layout/app-loading-provider";
import { useToast } from "../../components/toast/toast-context";
import { PrimaryButton } from "../../components/form/primary-button";
import { BottomSheet } from "../../components/form/company-dropdown";
import UserDeletedBottomSheetContent from "../../components/users/user-deleted-bottomsheet-content";

export const EditUserPage = () => {
  const { success, error, info, warning, show } = useToast();
  const [open, setOpen] = useState(false);
  const { showLoading, hideLoading } = useAppLoading();
  const [data, setData] = useState({});
  const urlParams = useParams();
  const [pageState, setPageState] = useState({
    isLoading: true,
    message: "Loading in progress",
  });
  const [unauthorised, setUnauthorised] = useState({ show: false });
  const { state, dispatch } = useContext(CognitoContext);
  const navigate = useNavigate();

  const getRecord = async () => {
    const userId = urlParams.id;
    if (!userId) return;

    const service = CoreServiceSelector.GetUserManagementService(state.session);
    if (!service) return;

    setPageState({
      ...pageState,
      userId,
      isLoading: true,
    });
    showLoading();
    const prefetched = consumeUserRecordPrefetch(userId);
    const result = await (prefetched ?? service.getUserRecord(userId)).catch((err) => {
      hideLoading();
      if (err?.response?.status === 401) {
        setUnauthorised({ show: true });
      }

      setPageState({
        isLoading: false,
        hasErrors: true,
      });
    });

    if (result) {
      hideLoading();
      setData(result);
      setPageState({
        ...pageState,
        isLoading: false,
      });
    }
  };

  useEffect(() => {
    setPageState({ isLoading: true });
    getRecord();
  }, [state]);

  const onSaveUser = async (model) => {
    showLoading();

    const service = CoreServiceSelector.GetUserManagementService(state.session);
    if (!service) return;

    const result = await service.saveUserRecord(model).catch((err) => {
      hideLoading();
      if (err?.response?.status === 401) {
        setUnauthorised({ show: true });
      }

      setPageState({
        isLoading: false,
        hasErrors: true,
      });
      error(err.response.data || "Something wrong happened");
    });

    if (result) {
      hideLoading();
      setData(result);
      success("User record saved successfully");
    }
  };

  const onEnableAccount = async (model) => {
    showLoading();
    const service = CoreServiceSelector.GetUserManagementService(state.session);
    if (!service) return;

    const result = await service.confirmUser(model.Guid).catch((err) => {
      hideLoading();
      if (err?.response?.status === 401) {
        setUnauthorised({ show: true });
      }

      setPageState({
        isLoading: false,
        hasErrors: true,
      });
      error(err.response.data || "Something wrong happened");
    });

    if (result) {
      setData(result);
      hideLoading();
      success("User account has been enabled");
    }
  };

  const onDeleteUserAccount = async (model) => {
    showLoading();

    const service = CoreServiceSelector.GetUserManagementService(state.session);
    if (!service) return;

    const result = await service.deleteUser(model.Guid).catch((err) => {
      hideLoading();
      if (err?.response?.status === 401) {
        setUnauthorised({ show: true });
      }

      setPageState({
        isLoading: false,
        hasErrors: true,
      });
      error(err.response.data || "Something wrong happened");
    });

    if (result) {
      hideLoading();
      setOpen(true);
    }
  };

  return (
    <AuthorisedPage>
      <PortalMainLayout isUnauthorised={unauthorised.show}>
        <div className="bg-black fixed left-1/2 -translate-x-1/2 top-0 z-50 h-20 !rounded-xl md:ml-[2.5rem] flex items-center w-full px-4 justify-between">
          <div className="md:pl-20">
            <span className="text-white font-headings-primary ml-2 text-xl">
              Edit user
            </span>
          </div>
          <div className="md:pr-8 ">
            <Link to="/users-list" className="flex items-center text-white ">
              <i className="bi bi-x-lg"></i>
            </Link>
          </div>
        </div>

        <BottomSheet
          open={open}
          title=" "
          onClose={() => {
            setOpen(false);
            navigate(AppRouteNames.usersList);
          }}
        >
          <UserDeletedBottomSheetContent open={open} setOpen={setOpen} data={data} />
        </BottomSheet>

        <div className="space-y-4 w-full md:w-3/4 lg:w-1/2 mx-auto my-8 max-w-[640px] md:px-4 mt-[10vh]">
          <PortalUserForm
            data={data}
            onSave={onSaveUser}
            onDelete={onDeleteUserAccount}
            onEnableAccount={onEnableAccount}
          />
        </div>
      </PortalMainLayout>
    </AuthorisedPage>
  );
};
